import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr, downloadCsv } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, UserPlus, KeyRound, Search, Copy, GraduationCap, Upload, AlertTriangle, Download, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Students() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [manage, setManage] = useState(null);
  const [del, setDel] = useState(null);
  const [editCreds, setEditCreds] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState(null); // { action }
  const [classFilter, setClassFilter] = useState("");

  const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const { data: coursesData } = useQuery({ queryKey: ["admin-courses"], queryFn: async () => (await api.get("/admin/courses")).data });
  const courses = coursesData?.courses ?? [];
  const { data: classesData } = useQuery({ queryKey: ["admin-classes"], queryFn: async () => (await api.get("/admin/students/classes")).data });
  const classes = classesData?.classes ?? [];
  const { data, isLoading } = useQuery({
    queryKey: ["admin-students", q, classFilter],
    queryFn: async () => (await api.get(`/admin/students?limit=100000&q=${encodeURIComponent(q)}${classFilter ? `&class_name=${encodeURIComponent(classFilter)}` : ""}`)).data,
  });

  const students = data?.students ?? [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-students"] }); qc.invalidateQueries({ queryKey: ["admin-classes"] }); };

  useEffect(() => { setSelected([]); }, [q, classFilter]);

  const allIds = students.map((s) => s.id);
  const allSelected = allIds.length > 0 && selected.length === allIds.length;
  const toggleAll = () => setSelected(allSelected ? [] : allIds);
  const toggleOne = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const clearSel = () => setSelected([]);
  const selectedStudents = students.filter((s) => selected.includes(s.id));

  const toggleStatus = async (s) => {
    const status = s.status === "active" ? "inactive" : "active";
    await api.patch(`/admin/students/${s.id}`, { status });
    toast.success(`Student ${status === "active" ? "activated" : "deactivated"}`);
    refresh();
  };

  const resetPw = async (s) => {
    const { data } = await api.post(`/admin/students/${s.id}/reset-password`);
    setResult({ name: s.name, temp_password: data.temp_password });
  };

  const exportLogins = async () => {
    try {
      const rows = [["Name", "Username", "Password", "Email", "Status"],
        ...students.map((s) => [s.name, s.username, s.password_plain || "", s.email, s.status])];
      downloadCsv("science-sorted-student-logins.csv", rows);
      toast.success(`Exported ${students.length} logins`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Admin</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Students</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={exportLogins} data-testid="export-logins-button" variant="outline">
            <Download className="h-4 w-4 mr-2" /> Export logins
          </Button>
          <Button onClick={() => setBulkOpen(true)} data-testid="bulk-upload-button" variant="outline">
            <Upload className="h-4 w-4 mr-2" /> Bulk upload
          </Button>
          <Button onClick={() => setAddOpen(true)} data-testid="add-student-button" className="bg-slate-900 hover:bg-slate-800">
            <UserPlus className="h-4 w-4 mr-2" /> Add student
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students…" className="pl-9 bg-white" data-testid="student-search-input" />
        </div>
        {classes.length > 0 && (
          <Select value={classFilter || "__all__"} onValueChange={(v) => setClassFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-56 bg-white" data-testid="class-filter-select"><SelectValue placeholder="All classes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All classes</SelectItem>
              {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {selected.length > 0 && (
        <div className="sticky top-2 z-10 flex items-center justify-between gap-3 flex-wrap rounded-xl border border-slate-900 bg-slate-900 px-4 py-2.5 text-white shadow-lg" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium" data-testid="bulk-selected-count">{selected.length} selected</span>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="secondary" onClick={() => setBulk({ action: "enroll" })} data-testid="bulk-enroll-button"><GraduationCap className="h-4 w-4 mr-1.5" /> Add courses</Button>
            <Button size="sm" variant="secondary" onClick={() => setBulk({ action: "remove_courses" })} data-testid="bulk-remove-courses-button">Remove courses</Button>
            <Button size="sm" variant="secondary" onClick={() => setBulk({ action: "activate" })} data-testid="bulk-activate-button">Activate</Button>
            <Button size="sm" variant="secondary" onClick={() => setBulk({ action: "deactivate" })} data-testid="bulk-deactivate-button">Deactivate</Button>
            <Button size="sm" onClick={() => setBulk({ action: "delete" })} data-testid="bulk-delete-button" className="bg-rose-600 hover:bg-rose-700 text-white"><Trash2 className="h-4 w-4 mr-1.5" /> Delete</Button>
            <button onClick={clearSel} data-testid="bulk-clear-button" className="text-slate-300 hover:text-white text-sm px-1">Clear</button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {isLoading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : students.length === 0 ? (
          <div className="py-16 text-center text-slate-500">No students yet. Add your first student.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-students" aria-label="Select all" />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="hidden sm:table-cell">Email</TableHead>
                <TableHead>Login</TableHead>
                <TableHead className="hidden md:table-cell">Courses</TableHead>
                <TableHead className="hidden lg:table-cell">Class</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {students.map((s) => (
                <TableRow key={s.id} data-testid={`student-row-${s.id}`} data-state={selected.includes(s.id) ? "selected" : undefined}>
                  <TableCell>
                    <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggleOne(s.id)} data-testid={`select-student-${s.id}`} aria-label={`Select ${s.name}`} />
                  </TableCell>
                  <TableCell className="font-medium">
                    {s.name}
                    <div className="text-xs text-slate-400 font-mono">{s.student_id}</div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-slate-500">{s.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-sm text-slate-800" data-testid={`username-${s.id}`}>{s.username}</span>
                      <button onClick={() => copyText(s.username)} data-testid={`copy-username-${s.id}`} className="text-slate-400 hover:text-slate-700" title="Copy username"><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="font-mono text-sm text-slate-500" data-testid={`password-${s.id}`}>{s.password_plain || "—"}</span>
                      {s.password_plain && <button onClick={() => copyText(s.password_plain)} data-testid={`copy-password-${s.id}`} className="text-slate-400 hover:text-slate-700" title="Copy password"><Copy className="h-3.5 w-3.5" /></button>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-slate-500">{s.enrolled_course_ids.length}</TableCell>
                  <TableCell className="hidden lg:table-cell text-slate-500" data-testid={`class-${s.id}`}>{s.class_name || "—"}</TableCell>
                  <TableCell>
                    <Badge variant={s.status === "active" ? "default" : "secondary"} className={s.status === "active" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}>
                      {s.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setManage(s)} data-testid={`manage-courses-${s.id}`} title="Courses">
                      <GraduationCap className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditCreds(s)} data-testid={`edit-creds-${s.id}`} title="Edit login">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => resetPw(s)} data-testid={`reset-pw-${s.id}`} title="Reset password">
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleStatus(s)} data-testid={`toggle-status-${s.id}`}>
                      {s.status === "active" ? "Deactivate" : "Activate"}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDel(s)} data-testid={`delete-student-${s.id}`} title="Delete student" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AddStudentDialog open={addOpen} onOpenChange={setAddOpen} courses={courses} onCreated={(r) => { setAddOpen(false); setResult(r); refresh(); }} />
      <BulkStudentDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={refresh} />
      <ManageCoursesDialog student={manage} courses={courses} onClose={() => setManage(null)} onChanged={refresh} />
      <DeleteStudentDialog student={del} onClose={() => setDel(null)} onDeleted={() => { setDel(null); refresh(); }} />
      <EditCredentialsDialog student={editCreds} onClose={() => setEditCreds(null)} onSaved={() => { setEditCreds(null); refresh(); }} />
      <BulkActionDialog bulk={bulk} students={selectedStudents} courses={courses} onClose={() => setBulk(null)}
        onDone={() => { setBulk(null); clearSel(); refresh(); }} />
      <ResultDialog result={result} onClose={() => setResult(null)} />
    </div>
  );
}

const BULK_LABELS = {
  enroll: "Add courses", remove_courses: "Remove courses",
  activate: "Activate students", deactivate: "Deactivate students", delete: "Delete students",
};

function BulkActionDialog({ bulk, students, courses, onClose, onDone }) {
  const [courseIds, setCourseIds] = useState([]);
  const [mode, setMode] = useState("add");
  const [busy, setBusy] = useState(false);
  const action = bulk?.action;
  const needsCourses = action === "enroll" || action === "remove_courses";

  useEffect(() => { if (bulk) { setCourseIds([]); setMode("add"); } }, [bulk]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/students/bulk", {
        student_ids: students.map((s) => s.id), action,
        course_ids: needsCourses ? courseIds : [], mode,
      });
      toast.success(`${BULK_LABELS[action]} — ${students.length} student${students.length === 1 ? "" : "s"} updated`);
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const disabled = busy || (needsCourses && courseIds.length === 0);
  const isDelete = action === "delete";

  return (
    <Dialog open={!!bulk} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-action-dialog">
        <DialogHeader>
          <DialogTitle>{action ? BULK_LABELS[action] : ""}</DialogTitle>
          <DialogDescription>
            {students.length} student{students.length === 1 ? "" : "s"} selected.
            {isDelete && " This permanently deletes them and all their study history. This can’t be undone."}
            {action === "enroll" && " Choose the courses to grant access to."}
            {action === "remove_courses" && " Choose the courses to remove access to. History is preserved."}
          </DialogDescription>
        </DialogHeader>

        {needsCourses && (
          <div className="space-y-3">
            {action === "enroll" && (
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 p-2">
                <button onClick={() => setMode("add")} data-testid="bulk-mode-add"
                  className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${mode === "add" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                  Add to existing
                </button>
                <button onClick={() => setMode("replace")} data-testid="bulk-mode-replace"
                  className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${mode === "replace" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                  Replace all
                </button>
              </div>
            )}
            <div className="space-y-2 max-h-56 overflow-auto">
              {courses.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={courseIds.includes(c.id)} data-testid={`bulk-course-${c.id}`}
                    onCheckedChange={(v) => setCourseIds(v ? [...courseIds, c.id] : courseIds.filter((x) => x !== c.id))} />
                  {c.name}
                </label>
              ))}
              {courses.length === 0 && <p className="text-sm text-slate-400">Create a course first.</p>}
            </div>
            {mode === "replace" && action === "enroll" && (
              <p className="text-xs text-amber-600">Replace removes access to any courses not selected above.</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="bulk-cancel">Cancel</Button>
          <Button onClick={submit} disabled={disabled} data-testid="bulk-confirm"
            className={isDelete ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-slate-900 hover:bg-slate-800"}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isDelete ? "Delete permanently" : `Apply to ${students.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddStudentDialog({ open, onOpenChange, courses, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [className, setClassName] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { name, course_ids: selected };
      if (email.trim()) payload.email = email.trim();
      if (className.trim()) payload.class_name = className.trim();
      const { data } = await api.post("/admin/students", payload);
      onCreated({ name, username: data.username, password: data.password });
      setName(""); setEmail(""); setClassName(""); setSelected([]);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-student-dialog">
        <DialogHeader><DialogTitle>Add student</DialogTitle>
          <DialogDescription>Their username is their first name and password is their last name (e.g. "Sarah Smith" → username <b>sarah</b>, password <b>Smith</b>).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Full name (first &amp; last)</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-student-name" placeholder="Sarah Smith" className="mt-1.5" /></div>
          <div><Label>Email (optional)</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-student-email" placeholder="auto-generated if left blank" className="mt-1.5" /></div>
          <div><Label>Class (optional)</Label><Input value={className} onChange={(e) => setClassName(e.target.value)} data-testid="new-student-class" placeholder="e.g. Year 12 Biology" className="mt-1.5" /></div>
          <div>
            <Label>Courses</Label>
            <div className="mt-2 space-y-2 max-h-48 overflow-auto">
              {courses.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={selected.includes(c.id)} data-testid={`enroll-checkbox-${c.id}`}
                    onCheckedChange={(v) => setSelected(v ? [...selected, c.id] : selected.filter((x) => x !== c.id))} />
                  {c.name}
                </label>
              ))}
              {courses.length === 0 && <p className="text-sm text-slate-400">Create a course first.</p>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !name.trim()} data-testid="create-student-submit" className="bg-slate-900 hover:bg-slate-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageCoursesDialog({ student, courses, onClose, onChanged }) {
  const toggle = async (cid, on) => {
    try {
      if (on) await api.post(`/admin/students/${student.id}/enrollments`, { course_id: cid });
      else await api.delete(`/admin/students/${student.id}/enrollments/${cid}`);
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const current = student?.enrolled_course_ids ?? [];
  return (
    <Dialog open={!!student} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="manage-courses-dialog">
        <DialogHeader><DialogTitle>Course access</DialogTitle>
          <DialogDescription>{student?.name} — toggle enrolment. History is preserved when access is removed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-72 overflow-auto">
          {courses.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
              <Checkbox defaultChecked={current.includes(c.id)} data-testid={`manage-enroll-${c.id}`}
                onCheckedChange={(v) => toggle(c.id, v)} />
              {c.name}
            </label>
          ))}
        </div>
        <DialogFooter><Button onClick={onClose} variant="secondary">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultDialog({ result, onClose }) {
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  return (
    <Dialog open={!!result} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="student-result-dialog">
        <DialogHeader><DialogTitle>Account ready</DialogTitle>
          <DialogDescription>Share these login details with {result?.name}.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between">
          <div><div className="text-xs text-slate-400">Username</div><div className="font-mono">{result?.username}</div></div>
          <Button size="sm" variant="ghost" onClick={() => copy(result?.username)}><Copy className="h-4 w-4" /></Button>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between">
          <div><div className="text-xs text-slate-400">Password (their last name)</div><div className="font-mono">{result?.password}</div></div>
          <Button size="sm" variant="ghost" onClick={() => copy(result?.password)}><Copy className="h-4 w-4" /></Button>
        </div>
        <DialogFooter><Button onClick={onClose} className="bg-slate-900 hover:bg-slate-800">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCredentialsDialog({ student, onClose, onSaved }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [className, setClassName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (student) { setUsername(student.username || ""); setPassword(student.password_plain || ""); setClassName(student.class_name || ""); }
  }, [student]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/students/${student.id}`, { username, password, class_name: className.trim() });
      toast.success("Student updated");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!student} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-creds-dialog">
        <DialogHeader><DialogTitle>Edit student</DialogTitle>
          <DialogDescription>Update {student?.name}'s login and class. They can sign in immediately with the new details.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Username</Label><Input value={username} onChange={(e) => setUsername(e.target.value)} data-testid="edit-username-input" className="mt-1.5 font-mono" /></div>
          <div><Label>Password</Label><Input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="edit-password-input" className="mt-1.5 font-mono" /></div>
          <div><Label>Class</Label><Input value={className} onChange={(e) => setClassName(e.target.value)} data-testid="edit-class-input" placeholder="e.g. Year 12 Biology" className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy || !username.trim() || !password.trim()} data-testid="save-creds-button" className="bg-slate-900 hover:bg-slate-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteStudentDialog({ student, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/students/${student.id}`);
      toast.success("Student deleted");
      onDeleted();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!student} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-student-dialog">
        <DialogHeader><DialogTitle>Delete student</DialogTitle>
          <DialogDescription>
            This permanently deletes <b>{student?.name}</b> and all their study history — progress, reviews and sessions. This can’t be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="cancel-delete-student">Cancel</Button>
          <Button onClick={remove} disabled={busy} data-testid="confirm-delete-student" className="bg-rose-600 hover:bg-rose-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkStudentDialog({ open, onOpenChange, onDone }) {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [skipExisting, setSkipExisting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setDone(null); if (fileRef.current) fileRef.current.value = ""; };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setDone(null); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/students/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/students/import/commit", { batch_id: preview.batch_id, skip_existing: skipExisting });
      setDone(data);
      toast.success(`Created ${data.created} students`);
      onDone();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-student-dialog">
        <DialogHeader><DialogTitle>Bulk upload students</DialogTitle>
          <DialogDescription>Upload a CSV or XLSX with columns <b>First name</b>, <b>Last name</b>, <b>Courses</b> (comma-separated course names) and optionally <b>Class</b> (e.g. "Year 12 Biology"). Each student's username is their first name and password is their last name.</DialogDescription>
        </DialogHeader>

        {!done && (
          <button onClick={() => downloadCsv("science-sorted-students-template.csv", [["First name", "Last name", "Class", "Courses"], ["Sarah", "Smith", "Year 12 Biology", "OCR A A-Level Biology, OCR A A-Level Chemistry"]])}
            data-testid="download-students-template"
            className="self-start inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-2">
            <Download className="h-4 w-4" /> Download CSV template
          </button>
        )}

        {!done && (
          <label className="block rounded-xl border-2 border-dashed border-slate-300 bg-white p-6 text-center cursor-pointer hover:border-slate-400 transition-colors">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="bulk-file-input" />
            <Upload className="h-6 w-6 text-slate-400 mx-auto" />
            <div className="mt-2 text-sm font-medium text-slate-700">{file ? file.name : "Choose a file"}</div>
            <div className="text-xs text-slate-400 mt-0.5">CSV or XLSX</div>
          </label>
        )}

        {busy && !preview && !done && <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}

        {preview && !done && (
          <div className="space-y-3" data-testid="bulk-preview">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-emerald-700 font-medium">{preview.valid} valid</span>
              {preview.error_count > 0 && <span className="text-rose-600">{preview.error_count} rows with errors</span>}
              {preview.existing_count > 0 && <span className="text-amber-600">{preview.existing_count} already exist</span>}
            </div>

            {preview.errors.length > 0 && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 max-h-32 overflow-auto text-sm text-rose-600">
                {preview.errors.map((e) => <div key={e.row}>Row {e.row}: {e.messages.join(", ")}</div>)}
              </div>
            )}

            <div className="rounded-lg border border-slate-200 overflow-auto max-h-72">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Username</TableHead><TableHead>Password</TableHead><TableHead>Class</TableHead><TableHead>Courses</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {preview.students.map((s, i) => (
                    <TableRow key={s.username} data-testid={`bulk-row-${i}`}>
                      <TableCell>{s.name} {s.existing && <Badge variant="secondary" className="ml-1">exists</Badge>}</TableCell>
                      <TableCell className="font-mono">{s.username}</TableCell>
                      <TableCell className="font-mono">{s.password}</TableCell>
                      <TableCell className="text-slate-500">{s.class_name || "—"}</TableCell>
                      <TableCell className="text-slate-500">
                        {s.course_names.join(", ") || "—"}
                        {s.unmatched_courses.length > 0 && (
                          <span className="ml-1 inline-flex items-center gap-1 text-amber-600 text-xs"><AlertTriangle className="h-3 w-3" /> unknown: {s.unmatched_courses.join(", ")}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={skipExisting} onCheckedChange={setSkipExisting} data-testid="skip-existing-switch" />
              <Label>Skip students that already exist</Label>
            </div>

            <Button onClick={commit} disabled={busy || preview.valid === 0} data-testid="commit-bulk-button" className="bg-slate-900 hover:bg-slate-800">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Create ${skipExisting ? preview.valid - preview.existing_count : preview.valid} accounts`}
            </Button>
          </div>
        )}

        {done && (
          <div className="space-y-3" data-testid="bulk-done">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-emerald-800">
              Created {done.created} students{done.skipped ? `, skipped ${done.skipped}` : ""}. Their logins are below.
            </div>
            {done.results.length > 0 && (
              <div className="rounded-lg border border-slate-200 overflow-auto max-h-72">
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Username</TableHead><TableHead>Password</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {done.results.map((r) => (
                      <TableRow key={r.username}><TableCell>{r.name}</TableCell><TableCell className="font-mono">{r.username}</TableCell><TableCell className="font-mono">{r.password}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <DialogFooter><Button onClick={() => { onOpenChange(false); reset(); }} className="bg-slate-900 hover:bg-slate-800">Done</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
